/**
 * App — root router + providers.
 * 7 route screens: Lobby · Table · TournamentRoute · Profile · Leaderboard · Daily · Invite
 */
import { lazy, Suspense, type ReactElement } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { TelegramProvider, useTelegram } from './providers/TelegramProvider'
import './theme/global.css'

// Lazy-load all routes to keep the initial bundle small.
const Lobby          = lazy(() => import('./routes/Lobby'))
const TableRoute     = lazy(() => import('./routes/TableRoute'))
const TournamentRoute = lazy(() => import('./routes/TournamentRoute'))
const Profile        = lazy(() => import('./routes/Profile'))
const Leaderboard    = lazy(() => import('./routes/Leaderboard'))
const Daily          = lazy(() => import('./routes/Daily'))
const Invite         = lazy(() => import('./routes/Invite'))

function RouteFallback(): ReactElement {
  return (
    <div className="route-fallback">
      <p>로딩 중...</p>
    </div>
  )
}

function NotInTelegram(): ReactElement {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: 32,
        textAlign: 'center',
        gap: 16,
      }}
    >
      <div style={{ fontSize: 48 }}>✈️</div>
      <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700 }}>
        Telegram 앱에서 열어주세요
      </div>
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        이 미니앱은 Telegram 내에서만 동작합니다
      </div>
    </div>
  )
}

function AppInner(): ReactElement {
  const { isReady, isDev } = useTelegram()

  // Guard: not in Telegram in prod
  if (!isDev) {
    const platform = window.Telegram?.WebApp?.platform
    if (!platform || platform === 'unknown') {
      return <NotInTelegram />
    }
  }

  if (!isReady) return <RouteFallback />

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/"                     element={<Lobby />} />
        <Route path="/table/:tableId"       element={<TableRoute />} />
        <Route path="/tournament/:tournamentId" element={<TournamentRoute />} />
        <Route path="/profile"              element={<Profile />} />
        <Route path="/leaderboard"          element={<Leaderboard />} />
        <Route path="/daily"                element={<Daily />} />
        <Route path="/invite"               element={<Invite />} />
        <Route path="*"                     element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

export default function App(): ReactElement {
  return (
    <TelegramProvider>
      <AppInner />
    </TelegramProvider>
  )
}
